import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { SaveAlt } from "@mui/icons-material";
import CustomAlert from "../../common/CustomAlert";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { CAN_ACCESS_APPLICATION } from "../../../constants/applicationConstants";
import CustomNumberField from "../../common/CustomNumberField";
import CustomAutocompleteField from "../../common/CustomAutocompleteField";
import { WOUND_TYPES } from "../../../constants/applicationConstants";
import { fetchAllWoundCodesByQuery, fetchWoundInfoByWoundCodes } from "../../../redux/slice/ivr/woundCodeSlice";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import BorderedSection from "../../common/BorderedSection";
import { useModalStyles } from "../../common/useModalStyles";
import { Scissors } from "lucide-react";
import { FieldLabel } from "../../../utils/Utility";
interface IncisionInfoProps {
  open: boolean;
  onClose: () => void;
  onAddIncisionInfo: (incisionData: any) => void;
  currentIncisionInfo?: any;
  from?: string;
  ivrId?: number;
}

export const incisionInfoInitialState: any = {
  woundType: WOUND_TYPES.SURGICAL_INCISION,
  // noOfApplications: "",
  eschar: "",
  exudate: "",
  drainage: "",
  erythema: "",
  skin: "",
  temperature: "",
  odor: "",
  edema: "",
  undermining: "",
  tunneling: '',
  pain: '',
  cellulitis: '',
  woundStage: '',
  debrided: '',
  length: "",
  width: "",
  depth: "",
  totalSize: "",
  estimateSurfaceArea: "",
  totalWoundVolume: "",
  procedureLocation: ''
};

const IVRFormIncisionInfoPopup: React.FC<IncisionInfoProps> = ({
  open,
  onClose,
  onAddIncisionInfo,
  currentIncisionInfo,
  from,
  ivrId
}) => {
  const { control, handleSubmit, reset, setValue, getValues, watch, clearErrors, trigger, formState: { errors } } = useForm({
    defaultValues: incisionInfoInitialState,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    dropdownWrapWithLabelSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const dispatch = useAppDispatch();
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [selectedIcdcodeOptions, setSelectedIcdcodeOptions] = useState<any[]>([]);
  const length = watch("length");
  const width = watch("width");
  const depth = watch("depth");
  const woundType = watch("woundType");
  const [exudateLevelList, setExudateLevelList] = useState<CodeTable[]>([]); 
  const [escharTypesList, setEscharTypesList] = useState<CodeTable[]>([]); 
  const [drainageTypesList, setDrainageTypesList] = useState<CodeTable[]>([]); 
  const [odorTypesList, setOdorTypesList] = useState<CodeTable[]>([]); 
  const [edemaTypesList, setEdemaTypesList] = useState<CodeTable[]>([]); 
  const [underminingTypesList, setUnderminingTypesList] = useState<CodeTable[]>([]); 
  const [tunnelingTypesList, setTunnelingTypesList] = useState<CodeTable[]>([]); 
  const [painTypesList, setPainTypesList] = useState<CodeTable[]>([]); 
  const [woundStageTypesList, setWoundStageTypesList] = useState<CodeTable[]>([]); 
  const [debridedTypesList, setDebridedTypesList] = useState<CodeTable[]>([]); 
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();

  useEffect(() => {
    if (open && !currentIncisionInfo) {
      reset(incisionInfoInitialState);
    } else if (currentIncisionInfo) {
      Object.keys(currentIncisionInfo).forEach((key) => {
        if (key === 'icd10' && typeof currentIncisionInfo[key] === 'string' && currentIncisionInfo[key].trim() !== "") {
          const icd10 = currentIncisionInfo[key]?.split(",").map((code: string) => code.trim());
          setValue(key, icd10, {
            shouldValidate: true,
          });
        } else if(key === 'exudate' && typeof currentIncisionInfo[key] === 'object') {
          setValue(key, currentIncisionInfo[key]?.props?.children, {
            shouldValidate: true,
          });
        } else {
          setValue(key, currentIncisionInfo[key], {
            shouldValidate: true,
          });
        }
      });
    }
  }, [open, currentIncisionInfo]);




  const onSubmit = (data: any) => {
    const formattedData = {
      ...data,
    };
    onAddIncisionInfo(formattedData);
    // setShowErrorAlert(true);
    reset(incisionInfoInitialState);
    onClose();

    let errorObj: SingleAlertInfo = {
      message: `Incision data has been successfully added to the ${ivrId ? "patient profile and this IVR form." : from === CAN_ACCESS_APPLICATION.PATIENT_MANAGEMENT ? "Patient." : `IVR Form.`}`,
      alertType: "success"
    };
    dispatch(setSingleAlertObj(errorObj));
  };

  const onError = (_errors: any, e: any) => {

    // const checked = specifyOtherDxCodes;
    let hasError = false;

    // if (checked) {
    //   hasError = false;
    //   clearErrors("eCode");
    //   clearErrors("iCode")
    //   clearErrors("lCode");
    // } else {
    //   hasError = true;
    //   trigger("eCode");
    //   trigger("iCode");
    //   trigger("lCode");
    // };

    if (!(Object.keys(errors).length > 0 || hasError)) {
      //   const data = getValues();
      //   onSubmit(data);
    };
  };

  const fetchWoundCodesByWoundKey = async (query: string, type?: string) => {
    //CS-1192 - Start
    if (query.length < 2 && type === "ICD10") {
      return []; // Don't call API if less than 2 characters
    }
    //CS-1192 - End
    try {
      const woundCodeType = type ?? '';
      const response = await dispatch(fetchAllWoundCodesByQuery({ searchQuery: query, woundType: woundCodeType, skipLoader: true })).unwrap();
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  };

  const fetchWoundsByWoundCodes = async (code: string, type?: string) => {
    try {
      const woundCodeType = type ?? '';
      const response = await dispatch(fetchWoundInfoByWoundCodes({ key: code, woundType: woundCodeType })).unwrap();
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  };




  // CS-992 Add Incision Type Surgical Incision

  const transformWoundCodeResponse = (res: any, woundCodeType?: string): any[] => {
    if (woundCodeType === "ICD10") {
      return (res?.data?.ICD10 || []).map((item: any) => ({
        value: item.woundKey,
        label: `${item.woundKey} - ${item.woundDisplayValue}`,
      }));
    } else if (woundCodeType === "CPTCODE") {
      return (res?.data?.CPTCODE || []).map((item: any) => ({
        value: item.woundKey,
        label: `${item.woundDisplayValue}`,
      }));
    } else if (woundCodeType === "ECODE") {
      return (res?.data?.ECODE || []).map((item: any) => ({
        value: item.woundKey,
        label: `${item.woundDisplayValue}`,
      }));
    } else if (woundCodeType === "LCODE") {
      return (res?.data?.LCODE || []).map((item: any) => ({
        value: item.woundKey,
        label: `${item.woundDisplayValue}`,
      }));
    } else if (woundCodeType === "ICODE") {
      return (res?.data?.ICODE || []).map((item: any) => ({
        value: item.woundKey,
        label: `${item.woundDisplayValue}`,
      }));
    } else if (woundCodeType === "ProcedureLocation") {
      //CS-1192 - Start
      return (res?.data?.ProcedureLocation || []).map((item: string) => ({
        value: item.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
        label: item
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
      }));
      //CS-1192 - End
    }
    return [];
  };


  React.useEffect(() => {
    const lengthValue = parseFloat(length);
    const widthValue = parseFloat(width);
    const depthValue = parseFloat(depth);

    if (!isNaN(lengthValue) && !isNaN(widthValue) && lengthValue > 0 && widthValue > 0) {
      const surfaceArea = (lengthValue * widthValue).toFixed(2);
      setValue("estimateSurfaceArea", `${surfaceArea}cm²`, { shouldValidate: true });
      // Keep totalSize / incisionMeasurement in sync for API / parent consumers (L x W)
      setValue("totalSize", surfaceArea, { shouldValidate: true });
      setValue("incisionMeasurement", surfaceArea, { shouldValidate: true });
      if (!isNaN(depthValue) && depthValue >= 0) {
        const volume = (lengthValue * widthValue * depthValue).toFixed(2);
        setValue("totalWoundVolume", `${volume}cm³`, { shouldValidate: true });
      } else {
        setValue("totalWoundVolume", "", { shouldValidate: true });
      }
    } else {
      setValue("estimateSurfaceArea", "", { shouldValidate: true });
      setValue("totalWoundVolume", "", { shouldValidate: true });
      setValue("totalSize", "", { shouldValidate: true });
      setValue("incisionMeasurement", "", { shouldValidate: true });
    }
  }, [length, width, depth, setValue, woundType]);

  useEffect(() => {
    if (!selectedIcdcodeOptions || selectedIcdcodeOptions.length === 0) return;
    setValue(
      'icdIds',
      selectedIcdcodeOptions
        .filter((opt): opt is { woundcodeId: any } => !!opt && opt.woundcodeId !== undefined && opt.woundcodeId !== null)
        .map(opt => opt.woundcodeId)
    );

  }, [selectedIcdcodeOptions]);





  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      setExudateLevelList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "ExudateLevel")
      );
      setEscharTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "EscharType"));
      setDrainageTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "DrainageType"));
      setOdorTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "OdorType"));
      setEdemaTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "EdemaType"));
      setUnderminingTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "UnderminingType"));
      setTunnelingTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "Tunneling Types"));
      setPainTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "PainType"));
      setWoundStageTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "WoundStageType"));
      setDebridedTypesList(getCodeTableValuesByTableName(allCodeTablesValuesMap, "DebridedType"));
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const exudateListOptions = useMemo(() => {
    return exudateLevelList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [exudateLevelList]);

  const escharTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...escharTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [escharTypesList]);

  const drainageTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...drainageTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [drainageTypesList]);

  const odorTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...odorTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [odorTypesList]);

  const edemaTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...edemaTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [edemaTypesList]);

  const underminingTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...underminingTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [underminingTypesList]);

  const tunnelingTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...tunnelingTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [tunnelingTypesList]);

  const painTypesOptions = useMemo(() => {
    return [
      { label: 'None', value: 'None' },
      ...painTypesList.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [painTypesList]);

  const woundStageTypesOptions = useMemo(() => {
    return woundStageTypesList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [woundStageTypesList]);

  const debridedTypesOptions = useMemo(() => {
    return debridedTypesList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [debridedTypesList]);

  const headerTitle = currentIncisionInfo?.incisionInfoKey
    ? `${currentIncisionInfo.incisionInfoKey} Incision Detail`
    : "Incision Detail";

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        onClick={(event) => {
          event.stopPropagation();
        }}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(modalTokens.accent)}>
            <Scissors size={22} color={modalTokens.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: modalTokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              {headerTitle}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
              ICD-10, location, measurements, and clinical observations
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit, onError)}
          sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <Box
            sx={{
              ...bodySx,
              gap: 1.5,
              py: 2,
              minHeight: 0,
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >
            <Box>
              <Grid container spacing={2}>
                {/* <Grid item xs={12} md={6}>
                  <CustomDropdownField
                    name="incisionType"
                    control={control}
                    options={incisionTypesOptions}
                    disabled={true}
                    label="Type"
                    onChangeSelect={handleChangeIncisionType}
                    sortOptions={false}
                    rules={{ required: "Type is required." }}
                  />
                </Grid> */}
                {/* CS-1193 - Start */}
                {/* {incisionType === "Surgical Incision" && (<>
                  <Grid item xs={12} md={6}>
                    <CustomDatePicker
                      control={control}
                      controllerName="dateOfSurgery"
                      label="Date Of Surgery"
                    // rules={{
                    //   validate: (value: any) => {
                    //     if (submitAction === "submitAction" && !value) {
                    //       return "Policy Holder DOB is required.";
                    //     }
                    //     return true;
                    //   }
                    // }}
                    onChange={() => {}}
                    />
                  </Grid>
                </>)} */}
                {/* CS-1193 - End */}
              </Grid>

              <Grid container spacing={1.5} sx={{ pt: 2 }}>
                <Grid item xs={12} md={6}>
                  <BorderedSection title="Details">
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Box>
                          <FieldLabel label="ICD-10 Code" required tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.icd10)}>
                            <CustomAutocompleteField
                              name="icd10"
                              control={control}
                              label=""
                              size="small"
                              fetchFunction={fetchWoundCodesByWoundKey}
                              initialFetchFunction={fetchWoundsByWoundCodes}
                              woundCodeType="ICD10"
                              //CS-1203 - Start
                              placeholderText={selectedIcdcodeOptions.length === 0 ? "Enter at least the first two characters to trigger suggestions" : ""}
                              //CS-1203 - End
                              transformResponse={transformWoundCodeResponse}
                              rules={{ required: "ICD10 Code is required." }}
                              initialCodes={getValues("icd10")}
                              onSelect={(selected) => setSelectedIcdcodeOptions(selected || [])}
                              isMultiple={true}
                            />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box>
                          <FieldLabel label="Location" required tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.procedureLocation)}>
                            <CustomAutocompleteField
                              name="procedureLocation"
                              control={control}
                              label=""
                              size="small"
                              fetchFunction={fetchWoundCodesByWoundKey}
                              initialFetchFunction={fetchWoundsByWoundCodes}
                              woundCodeType="ProcedureLocation"
                              transformResponse={transformWoundCodeResponse}
                              initialCodes={getValues("procedureLocation")}
                              rules={{ required: "Location is required." }}
                              isMultiple={false}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>
                <Grid item xs={12} md={6}>
                  <BorderedSection title="Measurements">
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <FieldLabel label="Length(cm)" required tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.length)}>
                            <CustomNumberField
                              controllerName="length"
                              control={control}
                              label=""
                              size="small"
                              allowDecimal={true}
                              decimalScale={2}
                              allowNegative={false}
                              rules={{ required: "Length is required." ,
                                  min: { value: 0.01, message: "Length must be greater than 0." } // CS-1605 
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box>
                          <FieldLabel label="Width(cm)" required tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.width)}>
                            <CustomNumberField
                              controllerName="width"
                              control={control}
                              label=""
                              size="small"
                              allowDecimal={true}
                              decimalScale={2}
                              allowNegative={false}
                              rules={{ required: "Width is required.",
                                min: { value: 0.01, message: "Width must be greater than 0." } // CS-1605 
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Box>
                          <FieldLabel label="Depth(cm)" required tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.depth)}>
                            <CustomNumberField
                              controllerName="depth"
                              control={control}
                              label=""
                              size="small"
                              allowDecimal={true}
                              decimalScale={2}
                              allowNegative={false}
                              rules={{ required: "Depth is required." }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box>
                          <FieldLabel label="Estimate Surface Area (LxW)" tokens={modalTokens} />
                          <Box sx={inputFieldSx(false)}>
                            <CustomTextField
                              controllerName="estimateSurfaceArea"
                              control={control}
                              label=""
                              size="small"
                              isDisabled={true}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box>
                          <FieldLabel label="Total Wound Volume (LxWxD)" tokens={modalTokens} />
                          <Box sx={inputFieldSx(false)}>
                            <CustomTextField
                              controllerName="totalWoundVolume"
                              control={control}
                              label=""
                              size="small"
                              isDisabled={true}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>
              </Grid>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6} mt={1.5}>
                  <BorderedSection title="Observations & Assessment">
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Erythema" tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.erythema)}>
                            <CustomTextField
                              controllerName="erythema"
                              control={control}
                              label=""
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Exudate" required tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.exudate)}>
                            <CustomDropdownField
                              name="exudate"
                              control={control}
                              options={exudateListOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                              rules={{ required: "Exudate is required." }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Skin" tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.skin)}>
                            <CustomTextField
                              controllerName="skin"
                              control={control}
                              label=""
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Temperature" tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.temperature)}>
                            <CustomTextField
                              controllerName="temperature"
                              control={control}
                              label=""
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Cellulitis" tokens={modalTokens} />
                          <Box sx={inputFieldSx(!!errors.cellulitis)}>
                            <CustomTextField
                              controllerName="cellulitis"
                              control={control}
                              label=""
                              size="small"
                            />
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Undermining" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.undermining)}>
                            <CustomDropdownField
                              name="undermining"
                              control={control}
                              options={underminingTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Tunneling" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.tunneling)}>
                            <CustomDropdownField
                              name="tunneling"
                              control={control}
                              options={tunnelingTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Pain" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.pain)}>
                            <CustomDropdownField
                              name="pain"
                              control={control}
                              options={painTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Wound Stage" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.woundStage)}>
                            <CustomDropdownField
                              name="woundStage"
                              control={control}
                              options={woundStageTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Debrided" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.debrided)}>
                            <CustomDropdownField
                              name="debrided"
                              control={control}
                              options={debridedTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>
                <Grid item xs={12} md={6} mt={1.5}>
                  <BorderedSection title="Symptoms">
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Eschar" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.eschar)}>
                            <CustomDropdownField
                              name="eschar"
                              control={control}
                              options={escharTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* CS-1441 */}
                      {/* <Grid item xs={12} md={6}>
                        <CustomDropdownField
                          name="drainage"
                          control={control}
                          options={drainageTypesOptions}
                          label="Drainage"
                          onChangeSelect={() => { }}
                          sortOptions={false}
                        />
                      </Grid> */}
                      {/* CS-1441 */}

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Odor" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.odor)}>
                            <CustomDropdownField
                              name="odor"
                              control={control}
                              options={odorTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box>
                          <FieldLabel label="Edema" tokens={modalTokens} />
                          <Box sx={dropdownWrapSx(!!errors.edema)}>
                            <CustomDropdownField
                              name="edema"
                              control={control}
                              options={edemaTypesOptions}
                              label=""
                              size="small"
                              onChangeSelect={() => { }}
                              sortOptions={false}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </BorderedSection>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={footerSx}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: isMobile ? "stretch" : "flex-end",
                flexDirection: isMobile ? "column-reverse" : "row",
                flexWrap: "wrap",
              }}
            >
              <Button
                type="submit"
                variant="contained"
                fullWidth={isMobile}
                startIcon={<SaveAlt sx={{ fontSize: "1rem !important" }} />}
                sx={primaryBtnSx}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                onClick={handleClose}
                sx={cancelBtnSx}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
      <CustomAlert
        open={showErrorAlert}
        onClose={() => setShowErrorAlert(false)}
        severity="info"
        message="Feature currently under development. Stay tuned for updates!"
      />
    </>
  );
};

export default IVRFormIncisionInfoPopup;
